/**
 * The paywall for building on a document the writer imported.
 *
 * Custom submissions are charged one flat fee — Asthra's audits and drafting on
 * top of an existing document — rather than per section, because their sections
 * are synthetic and were never priced. This dialog is shown after the library is
 * chosen and before the custom run is registered (see `startCustomFlow` in
 * `VariantCShell`); paying, or entering a valid promo code, lets the build
 * proceed. It mirrors `OrderDialog`'s promo affordance.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, useEffect, useState } from "react";

import { fonts, inkColors } from "../tokens";
import { formatUsd } from "../payments/money";
import { CUSTOM_UNLOCK_PRICE_MINOR } from "../payments/pricing";

const useStyles = makeStyles({
  surface: { maxWidth: "440px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "12px" },
  lede: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink, lineHeight: 1.5 },
  perks: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    margin: 0,
    paddingLeft: "18px",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
  },
  price: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "14px",
    fontWeight: 600,
    color: inkColors.ink,
    paddingTop: "8px",
    borderTop: "1px solid #ECE7F2",
  },
  taxRow: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  promo: { display: "flex", flexDirection: "column", gap: "4px", paddingTop: "4px" },
  promoRow: { display: "flex", gap: "6px", alignItems: "center" },
  promoInput: { flex: 1 },
  promoMsg: { fontFamily: fonts.body, fontSize: "11.5px" },
  promoErr: { color: "#B42318" },
});

export const UnlockAsthraDialog: FC<{
  open: boolean;
  /** True while a checkout is being opened. */
  busy?: boolean;
  /** Pay the flat unlock fee. */
  onPay: () => void;
  /** Apply a promo code; resolves ok=true when it waived payment. */
  onApplyPromo: (code: string) => Promise<{ ok: boolean; message?: string }>;
  onCancel: () => void;
}> = ({ open, busy = false, onPay, onApplyPromo, onCancel }) => {
  const styles = useStyles();
  const [code, setCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode("");
      setPromoMsg(null);
    }
  }, [open]);

  const applyPromo = async () => {
    const trimmed = code.trim();
    if (!trimmed || promoBusy) return;
    setPromoBusy(true);
    setPromoMsg(null);
    try {
      const result = await onApplyPromo(trimmed);
      if (!result.ok) setPromoMsg(result.message ?? "That code is not valid.");
    } catch {
      setPromoMsg("Could not check that code. Try again.");
    } finally {
      setPromoBusy(false);
    }
  };

  const busyAll = busy || promoBusy;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Unlock drafting and review for this document</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <span className={styles.lede}>
                Build on the document you imported with regulatory reviews and agent drafting. This is a one-time
                unlock for this document.
              </span>
              <ul className={styles.perks}>
                <li>Regulatory audits across your submission</li>
                <li>Section-by-section drafting and improvements</li>
                <li>Data-gap and consistency review</li>
              </ul>
              <div className={styles.taxRow}>
                <span>Tax</span>
                <span>calculated at payment</span>
              </div>
              <div className={styles.price}>
                <span>One-time unlock</span>
                <span>{formatUsd(CUSTOM_UNLOCK_PRICE_MINOR)}</span>
              </div>

              <div className={styles.promo}>
                <div className={styles.promoRow}>
                  <Input
                    className={styles.promoInput}
                    placeholder="Promo code"
                    value={code}
                    disabled={busyAll}
                    onChange={(_, data) => setCode(data.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void applyPromo();
                    }}
                  />
                  <Button
                    appearance="secondary"
                    disabled={busyAll || code.trim().length === 0}
                    onClick={() => void applyPromo()}
                  >
                    Apply
                  </Button>
                </div>
                {promoMsg !== null && <span className={`${styles.promoMsg} ${styles.promoErr}`}>{promoMsg}</span>}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busyAll}>
              Not now
            </Button>
            <Button appearance="primary" onClick={onPay} disabled={busyAll}>
              {`Pay ${formatUsd(CUSTOM_UNLOCK_PRICE_MINOR)} and continue`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UnlockAsthraDialog;
